import { BASE_PRECISION_EXP, BigNum, MarketType } from '@velocity-exchange/sdk';
import { useMemo, useState } from 'react';
import { getSplitOrders } from 'src/utils/scaledOrders';
import usePerpTradeConfirmationModalHandlers from 'src/components/TradeConfirmationModal/hooks/usePerpTradeConfirmationModalHandlers';
import useTradeConfirmationModal from 'src/components/TradeConfirmationModal/hooks/useTradeConfirmationModal';
import { TradeConfirmationModalProps } from 'src/components/TradeConfirmationModal/types';

export default function usePerpTradeConfirmationModal({
	tradeFormDetails,
}: TradeConfirmationModalProps) {
	const [isSubmitting, setIsSubmitting] = useState(false);

	const {
		orderType,
		postOnly,
		reduceOnly,
		bracketOrders,
		baseSizeStringValue,
		quoteSizeStringValue,
		priceBoxStringValue,
		secondaryPriceBoxStringValue,
		side: tradeSide,
		market: selectedMarketId,
		scaledOrders: { orderCount, sizeDistribution },
	} = tradeFormDetails;

	const {
		selectedMarket,
		actionValue,
		actionValueClassName,
		sizeDisplayLabel,
		sizeDisplayValue,
		estimatedLiquidationPriceUi,
		stepSizeDecimalPlaces,
		tickSizeDecimalPlaces,
		currentStepSize,
		priceBoxValue,
		secondaryPriceBoxValue,
	} = useTradeConfirmationModal({
		marketType: MarketType.PERP,
		orderType,
		tradeSide,
		baseSizeStringValue,
		quoteSizeStringValue,
		selectedMarketId,
		priceBoxStringValue,
		secondaryPriceBoxStringValue,
	});

	const baseSize = BigNum.fromPrint(baseSizeStringValue, BASE_PRECISION_EXP);

	const tradeCurrency = selectedMarket.uiSymbols.baseAssetSymbol;

	const currentStepSizeBigNum = useMemo(
		() => BigNum.from(currentStepSize, BASE_PRECISION_EXP),
		[currentStepSize]
	);

	const scaledOrders = useMemo(() => {
		return getSplitOrders({
			totalSize: baseSize,
			currentStepSize: currentStepSizeBigNum,
			orderCount,
			orderType,
			priceBoxValue,
			secondaryPriceBoxValue,
			sizeDistribution,
			tradeCurrency,
		});
	}, [
		baseSize,
		currentStepSizeBigNum,
		orderCount,
		orderType,
		priceBoxValue,
		secondaryPriceBoxValue,
		sizeDistribution,
		tradeCurrency,
	]);

	const { handleClose, handleConfirmation } =
		usePerpTradeConfirmationModalHandlers({
			selectedMarket,
			tradeFormDetails,
			setIsSubmitting,
		});

	return {
		orderType,
		priceBoxValue,
		secondaryPriceBoxValue,
		tradeSide,
		stepSizeDecimalPlaces,
		tickSizeDecimalPlaces,
		actionValue,
		sizeDisplayLabel,
		sizeDisplayValue,
		actionValueClassName,
		estimatedLiquidationPriceUi,
		reduceOnly,
		postOnly,
		orderCount,
		scaledOrders,
		bracketOrders,
		isSubmitting,
		handleClose,
		handleConfirmation,
	};
}
